import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Row selection for tables that pair checkboxes with bulk actions.
 *
 * Partial selection: the header checkbox only ever adds/removes the rows currently in
 * view (the table's filtered+sorted rows), so a selection made under one filter survives
 * narrowing the filter and toggling the header box there. It renders indeterminate while
 * the view is partially selected, and `hiddenCount` reports how many selected rows the
 * current filter hides (bulk actions still apply to them).
 */
export function useRowSelection<T>(visibleRows: T[], idOf: (row: T) => string) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const visibleIds = visibleRows.map(idOf);
  const visibleSelected = visibleIds.reduce((n, id) => n + (selected.has(id) ? 1 : 0), 0);
  const allSelected = visibleIds.length > 0 && visibleSelected === visibleIds.length;
  const someSelected = visibleSelected > 0 && !allSelected;
  const hiddenCount = selected.size - visibleSelected;

  // Callers hang the header checkbox off a memoised column def, so `toggleAll` routinely
  // outlives the render that created it: a filter change that leaves every derived flag
  // untouched (nothing selected, say) does not re-run that memo. Read the view through a
  // ref so the handler always acts on the rows on screen, never on a stale row set.
  const viewRef = useRef({ ids: visibleIds, allSelected });
  viewRef.current = { ids: visibleIds, allSelected };

  const toggleAll = useCallback(() => {
    const { ids, allSelected: viewFull } = viewRef.current;
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (viewFull) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, []);

  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return {
    selected,
    selectedIds: [...selected],
    allSelected,
    someSelected,
    hiddenCount,
    toggleAll,
    toggleOne,
    clear,
  };
}

/** Header checkbox that can render the tri-state "some rows selected" dash. */
export function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
  hiddenCount = 0,
  label = "select all",
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  hiddenCount?: number;
  label?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // `indeterminate` is a DOM property with no HTML attribute, so it has to be set here.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      aria-checked={indeterminate ? "mixed" : checked}
      checked={checked}
      onChange={onChange}
      title={
        hiddenCount > 0
          ? `Selects/clears the rows in view only. ${hiddenCount} selected ${
              hiddenCount === 1 ? "row is" : "rows are"
            } hidden by the current filter and stay selected.`
          : "Select every row in view"
      }
    />
  );
}

/** "n selected" label plus the count hidden by the active filter, for bulk-action bars. */
export function SelectionCount({ count, hiddenCount }: { count: number; hiddenCount: number }) {
  return (
    <span className="text-[12.5px] font-semibold whitespace-nowrap text-foreground">
      {count} selected
      {hiddenCount > 0 && (
        <span className="faint" style={{ fontWeight: 400, fontSize: 11.5, marginLeft: 6 }}>
          ({hiddenCount} hidden by filter)
        </span>
      )}
    </span>
  );
}
